import apiClient from "../utils/axios";
import { API_CONFIG } from "../config/api";

const E = API_CONFIG.ENDPOINTS;

/* ─────────────── AUTH ─────────────── */
export const authService = {
  login: async (credentials) =>
    (await apiClient.post(E.AUTH.LOGIN, credentials)).data,
  verifyMe: async () => (await apiClient.post(E.AUTH.VERIFY)).data,
  changePassword: async (current_password, new_password) =>
    (
      await apiClient.post(E.AUTH.CHANGE_PASSWORD, {
        current_password,
        new_password,
      })
    ).data,
};

/* ─────────────── DASHBOARD ─────────────── */
export const dashboardService = {
  getStats: async (body = {}) =>
    (await apiClient.post(E.DASHBOARD.STATS, body)).data,
};

/* ─────────────── LOOKUPS ─────────────── */
export const lookupsService = {
  getRoles: async () => (await apiClient.post(E.LOOKUPS.ROLES)).data,
  getCentres: async () => (await apiClient.post(E.LOOKUPS.CENTRES)).data,
};

/* ─────────────── USERS ─────────────── */
export const usersService = {
  getAll: async (body = {}) => (await apiClient.post(E.USERS.LIST, body)).data,
  getCount: async (body = {}) => (await apiClient.post(E.USERS.COUNT, body)).data,
  getById: async (id) => (await apiClient.post(E.USERS.DETAIL, { id })).data,
  create: async (data) => (await apiClient.post(E.USERS.CREATE, data)).data,
  update: async (id, data) =>
    (await apiClient.post(E.USERS.UPDATE, { ...data, id })).data,
  delete: async (id) => (await apiClient.post(E.USERS.DELETE, { id })).data,
};

/* ─────────────── ROLES & PERMISSIONS ─────────────── */
export const rolesService = {
  getAll: async (body = {}) => (await apiClient.post(E.ROLES.LIST, body)).data,
  getById: async (id) => (await apiClient.post(E.ROLES.DETAIL, { id })).data,
  create: async (data) => (await apiClient.post(E.ROLES.CREATE, data)).data,
  update: async (data) => (await apiClient.post(E.ROLES.UPDATE, data)).data,
  delete: async (id) => (await apiClient.post(E.ROLES.DELETE, { id })).data,
  getModulesWithPermissions: async (role_id) =>
    (await apiClient.post(E.ROLES.MODULES_WITH_PERMS, { role_id })).data,
  savePermissions: async (role_id, permissions) =>
    (await apiClient.post(E.ROLES.SAVE_PERMISSIONS, { role_id, permissions })).data,
  getMyPermissions: async () =>
    (await apiClient.post(E.ROLES.MY_PERMISSIONS)).data,
};

/* ─────────────── ACTIVITY LOG ─────────────── */
export const activityService = {
  getAll: async (body = {}) => (await apiClient.post(E.ACTIVITY.LIST, body)).data,
  getCount: async (body = {}) =>
    (await apiClient.post(E.ACTIVITY.COUNT, body)).data,
  markRead: async (ids) =>
    (await apiClient.post(E.ACTIVITY.MARK_READ, {
      ids: Array.isArray(ids) ? ids : [ids],
    })).data,
  markAllRead: async () => (await apiClient.post(E.ACTIVITY.MARK_ALL_READ, {})).data,
};

/* Generic lookup-style CRUD factory (list returns { list, total }). */
const makeCrudService = (endpoints) => ({
  getAll: async (body = {}) => (await apiClient.post(endpoints.LIST, body)).data,
  getById: async (id) => (await apiClient.post(endpoints.DETAIL, { id })).data,
  create: async (data) => (await apiClient.post(endpoints.CREATE, data)).data,
  update: async (data) => (await apiClient.post(endpoints.UPDATE, data)).data,
  updateStatus: async (id, is_active) =>
    (await apiClient.post(endpoints.UPDATE_STATUS, { id, is_active })).data,
  delete: async (id) => (await apiClient.post(endpoints.DELETE, { id })).data,
});

/* Build FormData transparently when any value is a File (for PDF uploads). */
const toFormData = (data = {}) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (v instanceof File) fd.append(k, v, v.name);
    else fd.append(k, v);
  });
  return fd;
};

/* ─────────────── CENTRES ─────────────── */
export const centresService = makeCrudService(E.CENTRES);

/* ─────────────── REGISTRATIONS ─────────────── */
export const registrationsService = {
  getAll: async (body = {}) =>
    (await apiClient.post(E.REGISTRATIONS.LIST, body)).data,
  getById: async (id) =>
    (await apiClient.post(E.REGISTRATIONS.DETAIL, { id })).data,
  approve: async (id) =>
    (await apiClient.post(E.REGISTRATIONS.APPROVE, { id })).data,
  reject: async (id, reject_reason) =>
    (await apiClient.post(E.REGISTRATIONS.REJECT, { id, reject_reason })).data,
  resend: async (id) =>
    (await apiClient.post(E.REGISTRATIONS.RESEND, { id })).data,
};

/* ─────────────── FAMILIES ─────────────── */
export const familiesService = {
  getAll: async (body = {}) => (await apiClient.post(E.FAMILIES.LIST, body)).data,
  getById: async (id) => (await apiClient.post(E.FAMILIES.DETAIL, { id })).data,
  update: async (data) => (await apiClient.post(E.FAMILIES.UPDATE, data)).data,
  updateStatus: async (id, is_active) =>
    (await apiClient.post(E.FAMILIES.UPDATE_STATUS, { id, is_active })).data,
  sendResetLink: async (id) =>
    (await apiClient.post(E.FAMILIES.RESET_LINK, { id })).data,
  delete: async (id) => (await apiClient.post(E.FAMILIES.DELETE, { id })).data,
};

/* ─────────────── CHILDREN ─────────────── */
export const childrenService = {
  getAll: async (body = {}) => (await apiClient.post(E.CHILDREN.LIST, body)).data,
  getById: async (id) => (await apiClient.post(E.CHILDREN.DETAIL, { id })).data,
  create: async (data) => (await apiClient.post(E.CHILDREN.CREATE, data)).data,
  update: async (data) => (await apiClient.post(E.CHILDREN.UPDATE, data)).data,
  delete: async (id) => (await apiClient.post(E.CHILDREN.DELETE, { id })).data,
};

/* ─────────────── FORMS ─────────────── */
export const formsService = {
  getAll: async (body = {}) => (await apiClient.post(E.FORMS.LIST, body)).data,
  getById: async (id) => (await apiClient.post(E.FORMS.DETAIL, { id })).data,
  update: async (data) => (await apiClient.post(E.FORMS.UPDATE, data)).data,
  updateStatus: async (id, is_active) =>
    (await apiClient.post(E.FORMS.UPDATE_STATUS, { id, is_active })).data,
};

/* ─────────────── SUBMISSIONS ─────────────── */
export const submissionsService = {
  getAll: async (body = {}) =>
    (await apiClient.post(E.SUBMISSIONS.LIST, body)).data,
  getById: async (id) =>
    (await apiClient.post(E.SUBMISSIONS.DETAIL, { id })).data,
  updateStatus: async (id, status, admin_note) =>
    (await apiClient.post(E.SUBMISSIONS.UPDATE_STATUS, {
      id,
      status,
      admin_note,
    })).data,
};

/* ─────────────── DOCUMENTS & POLICIES (PDF uploads) ─────────────── */
const makeFileDocService = (endpoints) => ({
  getAll: async (body = {}) => (await apiClient.post(endpoints.LIST, body)).data,
  getById: async (id) => (await apiClient.post(endpoints.DETAIL, { id })).data,
  create: async (data) =>
    (await apiClient.post(endpoints.CREATE, toFormData(data))).data,
  update: async (data) =>
    (await apiClient.post(endpoints.UPDATE, toFormData(data))).data,
  updateStatus: async (id, is_active) =>
    (await apiClient.post(endpoints.UPDATE_STATUS, { id, is_active })).data,
  delete: async (id) => (await apiClient.post(endpoints.DELETE, { id })).data,
});

export const documentsService = makeFileDocService(E.DOCUMENTS);
export const policiesService = makeFileDocService(E.POLICIES);

/* ─────────────── HANDBOOK ─────────────── */
export const handbookService = {
  getAll: async (body = {}) => (await apiClient.post(E.HANDBOOK.LIST, body)).data,
  getById: async (id) => (await apiClient.post(E.HANDBOOK.DETAIL, { id })).data,
  create: async (data) => (await apiClient.post(E.HANDBOOK.CREATE, data)).data,
  update: async (data) => (await apiClient.post(E.HANDBOOK.UPDATE, data)).data,
  delete: async (id) => (await apiClient.post(E.HANDBOOK.DELETE, { id })).data,
};

/* ─────────────── SETTINGS ─────────────── */
export const settingsService = {
  get: async () => (await apiClient.post(E.SETTINGS.GET, {})).data,
  save: async (data) =>
    (await apiClient.post(E.SETTINGS.SAVE, toFormData(data))).data,
};
